// Mis ventas del portal del vendedor — explorer de tres columnas del mockup
// `portal-vendedor-ventas.html`: filtros · tabla · detalle de la venta.
//
// `GET /sales?index=seller` pagina en el servidor de 25 en 25 con filtros, orden y contadores; la
// ficha sale del mismo ítem. El enlace `?sale=` que no está cargado se lee solo por id.
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { MasterDetailExplorer, type ExplorerSplit } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import { usePromotionSaleSlots } from '@/domains/retail/promotions-wizard';
import { INTERNAL_ERROR_MESSAGE, ApiError } from '@/api/errors';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useLoadedPaginationTable, type LoadedScope } from '@/shared/patterns/LoadedScope';
import { useCreditPortfolio } from './customers/api/useCreditPortfolio';
import { useCustomerDetail } from './customers/api/useSellerCustomers';
import { customerCreditFactsOf } from './customers/lib/customerCreditFacts';
import { useLinkedSale } from './mysales/api/useLinkedSale';
import { useSaleDevice } from './mysales/api/useSaleDevice';
import { useSellerSales } from './mysales/api/useSellerSales';
import { MySalesFiltersPanel } from './mysales/components/MySalesFiltersPanel';
import { MySalesTable } from './mysales/components/MySalesTable';
import { SaleDetailPanel, type SaleDetailTab } from './mysales/components/SaleDetailPanel';
import {
    brandOptions,
    countActiveFilters,
    DEFAULT_SORT,
    EMPTY_FILTERS,
    filterBySegment,
    filterSales,
    salesStats,
    segmentCounts,
    sortSales,
    type MySalesFilters,
    type SalesSegment,
    type SortState,
} from './mysales/lib/mySalesFilters';
import {
    loadedSalesNote,
    localOnlyFilterLabels,
    mySalesListErrorCopy,
    pillCounts,
    toSellerSalesFilterQuery,
} from './mysales/lib/mySalesServerQuery';

/** El reparto del mock a 1440px: 641px de tabla contra 357px de ficha. */
const SALES_SPLIT: ExplorerSplit = { list: 'minmax(0,9fr)', detail: 'minmax(300px,5fr)' };

/** Espera tras la última tecla antes de mandar la búsqueda al servidor. */
const SEARCH_DEBOUNCE_MS = 300;

/** Por qué no llegó la página siguiente: el mensaje tipado del silo o el genérico. */
function nextPageReason(error: unknown): string {
    return error instanceof ApiError ? error.msg : INTERNAL_ERROR_MESSAGE;
}

export function SalesMySales({ saleId }: { saleId?: string }) {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<MySalesFilters>(EMPTY_FILTERS);
    const [segment, setSegment] = useState<SalesSegment>('all');
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
    const [selectedId, setSelectedId] = useState<string | null>(saleId ?? null);
    const [linkedId, setLinkedId] = useState(saleId);
    const [tab, setTab] = useState<SaleDetailTab>('venta');
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    // El deep-link `?sale=` manda sobre lo elegido a mano: llegar por una URL nueva mueve la ficha
    // aunque el vendedor ya haya tocado otra fila.
    if (saleId !== linkedId) {
        setLinkedId(saleId);
        setSelectedId(saleId ?? null);
    }

    // La búsqueda filtra lo cargado al instante y viaja al servidor al dejar de teclear.
    const debouncedQuery = useDebounce(filters.query, SEARCH_DEBOUNCE_MS);
    const serverQuery = useMemo(
        () => toSellerSalesFilterQuery({ ...filters, query: debouncedQuery }, segment, sort),
        [filters, debouncedQuery, segment, sort],
    );
    const list = useSellerSales(serverQuery);

    // Lo que el servidor ya filtró se vuelve a pasar en memoria: es idempotente y cubre lo que él
    // no aplica (marca, chip «Crédito», monto) y el orden por columnas que no son la fecha.
    const panelRows = useMemo(() => filterSales(list.rows, filters), [list.rows, filters]);
    const visibleRows = useMemo(
        () => sortSales(filterBySegment(panelRows, segment), sort),
        [panelRows, segment, sort],
    );
    const table = useLoadedPaginationTable(visibleRows);
    const modelRows = table.getRowModel().rows;
    const pageRows = useMemo(() => modelRows.map((row) => row.original), [modelRows]);
    const stats = useMemo(() => salesStats(visibleRows), [visibleRows]);
    const brands = useMemo(() => brandOptions(list.rows), [list.rows]);

    const scope: LoadedScope = { loaded: list.rows.length, hasMore: list.hasNextPage };
    const loadedCounts = useMemo(() => segmentCounts(panelRows), [panelRows]);
    const pills = pillCounts({ counters: list.counters, loadedCounts, scope });
    const localFilters = localOnlyFilterLabels(filters);
    const loadedNote = loadedSalesNote({
        scope,
        localFilters,
        localSort: sort.key !== 'soldAt',
    });

    // El enlace que no llegó entre lo cargado se lee por id; sin él, la ficha dice que no existe.
    const linkOutsideLoaded =
        linkedId !== undefined &&
        selectedId === linkedId &&
        !list.isPending &&
        !list.rows.some((sale) => sale.saleId === linkedId);
    const linked = useLinkedSale(linkOutsideLoaded ? linkedId : null);
    const linkNotFound = linked.notFound;

    /**
     * La venta abierta: la elegida si sigue visible, la del enlace leída aparte, o la primera visible
     * si el filtro se llevó la elegida. Con el enlace roto se abre la nada.
     */
    const openedSale = useMemo(() => {
        const visible = visibleRows.find((sale) => sale.saleId === selectedId);
        if (visible) return visible;
        if (linked.sale) return linked.sale;
        if (linkNotFound || linked.failed || linked.isLoading) return null;
        return visibleRows[0] ?? null;
    }, [visibleRows, selectedId, linked.sale, linked.isLoading, linked.failed, linkNotFound]);
    const openedId = openedSale?.saleId ?? null;

    // El 360 solo se pide cuando hay a quién consultar; la pestaña Cliente lee de aquí.
    const customer = useCustomerDetail(openedSale?.retailCustomerId ?? null);

    // El catálogo de la tienda completa la ficha del equipo, y con `''` la query queda apagada
    // mientras no haya venta abierta. Con `'portal'` comparte cache con la pantalla de Catálogo.
    const catalog = useCatalog(openedSale ? 'portal' : '');
    const promotionSlots = usePromotionSaleSlots('STORE');

    // El nivel, el SCI y el historial salen de la CARTERA, que se lee sin `loan.credit.read`: es el
    // único permiso de crédito que el vendedor tiene. Se pide solo por la venta abierta.
    const openedCustomerId = openedSale?.retailCustomerId ?? null;
    const portfolioIds = useMemo(
        () => (openedCustomerId ? [openedCustomerId] : []),
        [openedCustomerId],
    );
    const portfolio = useCreditPortfolio(portfolioIds, list.isPending);
    const creditFacts = customerCreditFactsOf({
        cell: openedCustomerId ? portfolio.byCustomer.get(openedCustomerId) : undefined,
        portfolioState: portfolio.state,
        creditEntry: null,
    });

    // El expediente del candado es una lectura APARTE: se pide por la venta abierta y solo si trae
    // teléfono, nunca por fila de la tabla.
    const device = useSaleDevice(
        openedSale?.saleId ?? null,
        openedSale ? hasEquipment(openedSale) : true,
    );

    // Cambiar filtro, píldora u orden es otra consulta desde la página 1 del servidor, y la tabla
    // vuelve a su primera página sola al cambiar las filas.
    const applyFilters = (next: MySalesFilters) => {
        setFilters(next);
    };

    const applySegment = (next: SalesSegment) => {
        setSegment(next);
    };

    /** Elegir a mano mueve TAMBIÉN el `?sale=` de la URL: recargar abría la ficha vieja. */
    const selectRow = (nextSaleId: string) => {
        setSelectedId(nextSaleId);
        void navigate({
            to: '/sales/$section',
            params: { section: 'my-sales' },
            search: { sale: nextSaleId },
            replace: true,
        });
    };

    const openCustomer = (retailCustomerId: string) => {
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: { customer: retailCustomerId },
        });
    };

    return (
        <ScreenFrame>
            <MasterDetailExplorer
                className="m-2 rounded-[10px] border border-border bg-card"
                split={SALES_SPLIT}
                filtersCollapsed={filtersCollapsed}
                filters={
                    <MySalesFiltersPanel
                        filters={filters}
                        brands={brands}
                        onChange={applyFilters}
                        partialScope={list.hasNextPage}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((v) => !v);
                        }}
                    />
                }
                list={
                    <MySalesTable
                        table={table}
                        pageRows={pageRows}
                        shownCount={visibleRows.length}
                        totalCount={
                            list.counters && !list.counters.truncated && localFilters.length === 0
                                ? list.counters[segment]
                                : null
                        }
                        scope={scope}
                        stats={stats}
                        segment={segment}
                        pills={pills}
                        onSegmentChange={applySegment}
                        loadedNote={loadedNote}
                        hasActiveFilters={countActiveFilters(filters) > 0 || segment !== 'all'}
                        sort={sort}
                        onSortChange={setSort}
                        selectedId={openedId}
                        onSelect={selectRow}
                        isLoading={list.isPending}
                        errorCopy={list.error ? mySalesListErrorCopy(list.error) : null}
                        onRetry={list.retry}
                        hasNextPage={list.hasNextPage}
                        isFetchingNextPage={list.isFetchingNextPage}
                        onLoadMore={list.fetchNextPage}
                        nextPageError={
                            list.nextPageError === null ? null : nextPageReason(list.nextPageError)
                        }
                    />
                }
                detail={
                    <SaleDetailPanel
                        sale={openedSale}
                        customer={{
                            data: customer.data,
                            isLoading: customer.isPending,
                            error: customer.error,
                        }}
                        credit={creditFacts}
                        catalog={{
                            items: catalog.data?.items,
                            isLoading: catalog.isLoading,
                            isError: catalog.isError,
                        }}
                        device={device.state}
                        onRetryDevice={device.refetch}
                        tab={tab}
                        onTabChange={setTab}
                        onOpenCustomer={openCustomer}
                        notFound={linkNotFound}
                        onRetryLink={linked.failed ? linked.retry : undefined}
                        slots={promotionSlots}
                    />
                }
            />
        </ScreenFrame>
    );
}
